import { setTriageIdentity, type TriageStaff } from '@elva/ui';
import { useMemo } from 'react';
import { useLocationId } from '@/data/useLocationId';
import { useBootstrap } from '@/features/workspace/hooks/useBootstrap';
import { useTriageAssignees } from './useTriageAssignees';

function normalizeName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Feeds the triage staff roster + current user into @elva/ui from
 * `GET /api/triage/assignees`. Ids are organization.User PKs (as strings), the
 * same values PATCH takes as `assignee_id` and rows carry as `assigneeId`.
 * The registry write is idempotent and done during render (not in an effect) so
 * children rendered in the same pass already see the loaded roster.
 */
export function useTriageIdentity(): void {
  const { data } = useTriageAssignees();
  const locationId = useLocationId();
  const { data: bootstrap } = useBootstrap(locationId ?? null);
  const user = bootstrap?.user;

  useMemo(() => {
    if (!data) return;

    const staff: TriageStaff[] = data.items.map((a) => {
      const name = a.name || [a.first_name, a.last_name].filter(Boolean).join(' ');
      return {
        id: String(a.id),
        name,
        initials: initialsOf(name),
        // The backend can return a bare id here; that isn't a role label.
        role: /^\d+$/.test(a.role) ? '' : a.role,
        color: 'var(--ai-3)',
      };
    });

    let me = data.items.find((a) => a.is_me);
    if (!me && user) {
      // Fallback when the backend couldn't map the JWT to a user: match the signed-in
      // user's name, and only when it is unambiguous.
      const fullName = normalizeName([user.first_name, user.last_name].filter(Boolean).join(' '));
      if (fullName) {
        const matches = data.items.filter(
          (a) => normalizeName(a.name || `${a.first_name} ${a.last_name}`) === fullName,
        );
        if (matches.length === 1) me = matches[0];
      }
    }

    setTriageIdentity({ staff, currentUserId: me ? String(me.id) : '' });
  }, [data, user]);
}
